/**
 * API 数据层 — 与后台 PHP API 对接
 * 开发时通过 Vite 代理转发 /admin/api -> http://shijiarongyan.com/admin/api
 * 生产环境直接同域请求
 */

const API_BASE = '/admin/api';

// ---------- 通用 fetch 封装 ----------
async function fetchJSON<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = params
    ? `${API_BASE}/${endpoint}?${new URLSearchParams(params)}`
    : `${API_BASE}/${endpoint}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
  return res.json();
}

// ================ 类型定义 ================

export interface Slide {
  id: number | string;
  title: string;
  subtitle: string;
  description: string;
  cta: string;          // 映射自 cta_text
  ctaLink: string;      // 映射自 cta_link
  image: string;
}

export interface AboutStat {
  number: string;
  label: string;
}

export interface AboutData {
  tag: string;
  title: string;
  features: string[];
  stats: AboutStat[];
}

export interface FeatureItem {
  icon: string;
  title: string;
  description: string;
  metric: string;       // 映射自 stat
  image: string;
}

export interface HomeProduct {
  id: string;
  name: string;
  spec: string;
  description: string;
  image: string;
  badge: string;
  category: string;
}

export interface TestimonialItem {
  quote: string;
  name: string;
  role: string;
  avatar: string;
  rating: number;
}

export interface HomeData {
  slides: Slide[];
  about: AboutData;
  features: { tag: string; title: string; items: FeatureItem[] };
  products: { tag: string; title: string; items: HomeProduct[] };
  testimonials: { title: string; rating: string; items: TestimonialItem[] };
  cta: { title: string; subtitle: string; button: string; bgImage: string };
}

export interface ProductItem {
  id: string;
  name: string;
  spec: string;
  category: string;
  description: string;
  image: string;
  badge: string;
  inStock: boolean;
}

export interface ProductListResult {
  items: ProductItem[];
  total: number;
  page: number;
  totalPages: number;
  categories: { id: number; name: string; slug: string }[];
}

export interface ProductDetail {
  id: string;
  name: string;
  spec: string;
  category: string;
  description: string;
  image: string;
  badge: string;
  inStock: boolean;
  fullDescription: string;
  features: { title: string; desc: string }[];
  specifications: { label: string; value: string }[];
  applications: string[];
  gallery: string[];
  relatedIds: string[];
}

export interface NewsItem {
  id: number;
  title: string;
  content?: string;
  excerpt: string;       // 可能后端用 summary 字段
  image: string;
  news_date: string;     // 后台字段名
  category: string;      // 映射自 category_name
}

export interface NewsListResult {
  items: NewsItem[];
  total: number;
  page: number;
  totalPages: number;
  categories: { name: string; slug: string }[];
}

export interface SolutionItem {
  id: number | string;
  title: string;
  shortTitle: string;
  description: string;
  features: string[];
  image: string;
  stat: string;
  statLabel: string;
  icon: string;
  color: string;
}

export interface DownloadItem {
  id: number | string;
  title: string;
  description: string;
  fileType: string;     // 可能从文件扩展名推断
  fileSize: string;
  icon: string;
  category: string;      // 映射自 category_name
  file_url?: string;
}

export interface DownloadListResult {
  items: DownloadItem[];
  categories: { name: string; slug: string }[];
}

export interface ContactInfo {
  company_name?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  company_wechat?: string;
  company_whatsapp?: string;
  company_telegram?: string;
  wechat_qr?: string;
  whatsapp_qr?: string;
  telegram_qr?: string;
}

/** 站点全局设置（从 site_settings 表读取） */
export interface SiteSettings {
  site_name?: string;
  site_logo?: string;
  copyright?: string;
  site_keywords?: string;
  site_description?: string;
  company_name?: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  company_wechat?: string;
  company_whatsapp?: string;
  company_telegram?: string;
  wechat_qr?: string;
  whatsapp_qr?: string;
  telegram_qr?: string;
}

// ================ 工具函数 ================

/** 将后台存储的相对路径解析为可访问的 URL（后台文件位于 /admin/ 下） */
export function resolveAssetPath(path: string | undefined | null): string | null {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) return path;
  if (path.startsWith('/')) return path;
  return `/admin/${path}`;
}

// ================ API 函数 ================

/** 获取首页全部数据 */
export async function fetchHomeData(): Promise<HomeData> {
  const raw = await fetchJSON<any>('home.php');

  // 转换 slides
  const slides: Slide[] = (raw.slides || []).map((s: any) => ({
    id: s.id,
    title: s.title || '',
    subtitle: s.subtitle || '',
    description: s.description || '',
    cta: s.cta_text || '',
    ctaLink: s.cta_link || '/products',
    image: resolveAssetPath(s.image) || s.image || '',
  }));

  // 转换 about（从 home_about 表的 feature1~4 字段提取）
  const featureTexts: string[] = [];
  if (raw.about) {
    for (let i = 1; i <= 4; i++) {
      const title = raw.about[`feature${i}_title`];
      const desc  = raw.about[`feature${i}_desc`];
      if (title) featureTexts.push(desc ? `${title}: ${desc}` : title);
    }
  }
  const about: AboutData = {
    tag: raw.about?.tag || 'About Us',
    title: raw.about?.title || 'Pioneering Wireless Communication Since 2005',
    features: featureTexts,
    stats: (raw.about?.stats || []).map((s: any) => ({
      number: s.value || s.number || '',
      label: s.label || '',
    })),
  };

  // 转换 features
  const featureItems: FeatureItem[] = (raw.features || []).map((f: any) => ({
    icon: f.icon || 'ri-shield-check-line',
    title: f.title || '',
    description: f.description || '',
    metric: f.stat || f.metric || '',
    image: f.image || '',
  }));

  // 转换 products
  const homeProducts: HomeProduct[] = (raw.products || []).map((p: any) => ({
    id: String(p.product_id || p.id || ''),
    name: p.name || '',
    spec: p.spec || '',
    description: p.description || '',
    image: resolveAssetPath(p.image) || p.image || '',
    badge: p.badge || '',
    category: p.category || '',
  }));

  // 转换 testimonials
  const testimonialItems: TestimonialItem[] = (raw.testimonials || []).map((t: any) => ({
    quote: t.quote || '',
    name: t.name || '',
    role: t.role || '',
    avatar: t.avatar || '',
    rating: Number(t.rating) || 5,
  }));

  // 转换 cta
  const cta = {
    title: raw.cta?.cta_title || '',
    subtitle: raw.cta?.cta_subtitle || '',
    button: raw.cta?.cta_button || 'Get In Touch',
    bgImage: resolveAssetPath(raw.cta?.cta_bg) || raw.cta?.cta_bg || '',
  };

  return {
    slides,
    about,
    features: {
      tag: 'Our Tech',
      title: 'Built for Performance in Every Environment',
      items: featureItems,
    },
    products: {
      tag: 'View All',
      title: 'Professional Grade Two-Way Radios',
      items: homeProducts,
    },
    testimonials: {
      title: 'What Clients Say',
      rating: '4.9',
      items: testimonialItems,
    },
    cta,
  };
}

/** 获取产品列表 */
export async function fetchProducts(params?: {
  category?: string;
  page?: number;
  limit?: number;
}): Promise<ProductListResult> {
  const query: Record<string, string> = {};
  if (params?.category) query.category = params.category;
  if (params?.page) query.page = String(params.page);
  if (params?.limit) query.limit = String(params.limit);

  const raw = await fetchJSON<any>('products.php', query);

  const items: ProductItem[] = (raw.items || []).map((p: any) => ({
    id: String(p.product_id || p.id || ''),
    name: p.name || '',
    spec: p.spec || '',
    category: p.category || '',
    description: p.description || '',
    image: resolveAssetPath(p.image) || p.image || '',
    badge: p.badge || '',
    inStock: p.inStock !== undefined ? p.inStock : true,
  }));

  return {
    items,
    total: raw.total || 0,
    page: raw.page || 1,
    totalPages: raw.totalPages || 1,
    categories: raw.categories || [],
  };
}

/** 获取产品详情 */
export async function fetchProductDetail(id: string): Promise<ProductDetail> {
  const raw = await fetchJSON<any>('products.php', { id });
  return {
    id: String(raw.product_id || raw.id || ''),
    name: raw.name || '',
    spec: raw.spec || '',
    category: raw.category || '',
    description: raw.description || '',
    image: resolveAssetPath(raw.image) || raw.image || '',
    badge: raw.badge || '',
    inStock: raw.inStock !== undefined ? raw.inStock : true,
    fullDescription: raw.fullDescription || raw.description || '',
    features: raw.features || [],
    specifications: raw.specifications || [],
    applications: raw.applications || [],
    gallery: (raw.gallery || []).map((g: string) => resolveAssetPath(g) || g),
    relatedIds: raw.relatedIds || [],
  };
}

/** 获取新闻列表 */
export async function fetchNews(params?: {
  category?: string;
  page?: number;
  limit?: number;
}): Promise<NewsListResult> {
  const query: Record<string, string> = {};
  if (params?.category) query.category = params.category;
  if (params?.page) query.page = String(params.page);
  if (params?.limit) query.limit = String(params.limit);

  const raw = await fetchJSON<any>('news.php', query);

  // 转换新闻条目
  const items: NewsItem[] = (raw.items || []).map((n: any) => ({
    id: n.id,
    title: n.title || '',
    excerpt: n.excerpt || n.summary || n.content?.substring(0, 150) || '',
    image: resolveAssetPath(n.image) || n.image || '',
    news_date: n.news_date || '',
    category: n.category_name || '',
    content: n.content || '',
  }));

  return {
    items,
    total: raw.total || 0,
    page: raw.page || 1,
    totalPages: raw.totalPages || 1,
    categories: raw.categories || [],
  };
}

/** 获取解决方案列表 */
export async function fetchSolutions(): Promise<SolutionItem[]> {
  const raw = await fetchJSON<any>('solutions.php');

  return (raw.items || []).map((s: any) => ({
    id: s.id,
    title: s.title || '',
    shortTitle: s.short_title || s.title || '',
    description: s.description || '',
    features: s.features || [],
    image: resolveAssetPath(s.image) || s.image || '',
    stat: s.stat || '',
    statLabel: s.stat_label || '',
    icon: s.icon || 'ri-building-line',
    color: s.color || 'primary',
  }));
}

/** 获取下载中心数据 */
export async function fetchDownloads(params?: {
  category?: string;
}): Promise<DownloadListResult> {
  const query: Record<string, string> = {};
  if (params?.category) query.category = params.category;

  const raw = await fetchJSON<any>('downloads.php', query);

  const items: DownloadItem[] = (raw.items || []).map((d: any) => ({
    id: d.id,
    title: d.title || '',
    description: d.description || '',
    fileType: d.file_type || 'PDF',
    fileSize: d.file_size || '',
    icon: d.icon || 'ri-file-text-line',
    category: d.category_name || '',
    file_url: resolveAssetPath(d.file_url) || d.file_url || '',
  }));

  return {
    items,
    categories: raw.categories || [],
  };
}

/** 获取联系信息 */
export async function fetchContactInfo(): Promise<ContactInfo> {
  return fetchJSON<ContactInfo>('contact.php');
}

/** 获取站点全局设置（Logo、名称、版权等） */
export async function fetchSiteSettings(): Promise<SiteSettings> {
  return fetchJSON<SiteSettings>('contact.php');
}

/** 提交联系表单 */
export async function submitContactForm(data: {
  name: string;
  email: string;
  company: string;
  message: string;
}): Promise<{ success: boolean }> {
  const url = `${API_BASE}/contact.php`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('提交失败，请稍后重试');
  return res.json();
}
